import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { configureApiClient } from '@/api/client';
import type { Perfil } from '@/lib/auth/rbac';

interface UsuarioAutenticado {
  id: string;
  nome: string;
  email: string;
  perfil: Perfil;
  permissoes: string[];
}

interface AuthContextValue {
  usuario: UsuarioAutenticado | null;
  accessToken: string | undefined;
  isAuthenticated: boolean;
  login: (token: string, usuario: UsuarioAutenticado) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioAutenticado | null>(null);
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);

  const logout = useCallback(() => {
    setUsuario(null);
    setAccessToken(undefined);
  }, []);

  const login = useCallback((token: string, user: UsuarioAutenticado) => {
    setAccessToken(token);
    setUsuario(user);
  }, []);

  useEffect(() => {
    configureApiClient({
      getAccessToken: () => accessToken,
      setAccessToken: (token) => setAccessToken(token),
      onUnauthorized: () => logout(),
    });
  }, [accessToken, logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      usuario,
      accessToken,
      isAuthenticated: Boolean(usuario && accessToken),
      login,
      logout,
    }),
    [usuario, accessToken, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}
